import { useMemo, useState, type ChangeEvent, type FormEvent } from "react";
import type { AppLanguage } from "../app/language";
import type { Expense, ExpenseCreateInput, ExpenseVendor, ExpenseVendorCreateInput } from "../app/types";
import { AnimatedModal } from "../app/ui";

const MAX_INVOICE_FILE_BYTES = 10 * 1024 * 1024;
const MAX_INVOICE_TOTAL_BYTES = 25 * 1024 * 1024;
const NEW_VENDOR_VALUE = "__new_vendor__";

type ExpenseTrackerViewProps = {
  vendors: ExpenseVendor[];
  expenses: Expense[];
  customers?: { id: string; company: string }[];
  currency: string;
  language: AppLanguage;
  isLoading?: boolean;
  canManageInvoices?: boolean;
  canViewAllExpenses?: boolean;
  canEditExpenses?: boolean;
  canDeleteExpenses?: boolean;
  canEditAllExpenses?: boolean;
  canCreateVendors?: boolean;
  currentUserEmail?: string | null;
  members?: { email: string; name: string }[];
  onCreateExpense: (input: ExpenseCreateInput, invoices: File[]) => void | Promise<void>;
  onUpdateExpense?: (expenseId: string, input: ExpenseCreateInput, invoices: File[]) => void | Promise<void>;
  onDeleteExpense: (expenseId: string) => void;
  onDeleteInvoice?: (expenseId: string, invoiceKey: string) => void;
  onCreateVendor?: (input: ExpenseVendorCreateInput) => ExpenseVendor | void;
};

function formatFileSize(bytes: number) {
  if (bytes >= 1024 * 1024) {
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  if (bytes >= 1024) {
    return `${Math.round(bytes / 1024)} KB`;
  }

  return `${bytes} B`;
}

function getCurrentMonthKey() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
}

function getTodayDate() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
}

function getMonthKey(expenseDate: string) {
  return expenseDate.slice(0, 7);
}

function formatMonthLabel(monthKey: string, language: AppLanguage) {
  const parts = monthKey.split("-");
  const year = Number.parseInt(parts[0] ?? "", 10);
  const month = Number.parseInt(parts[1] ?? "", 10);

  if (!Number.isFinite(year) || !Number.isFinite(month)) {
    return monthKey;
  }

  const date = new Date(Date.UTC(year, Math.max(0, month - 1), 1));
  return new Intl.DateTimeFormat(language === "ja" ? "ja-JP" : "en-US", { month: "long", year: "numeric", timeZone: "UTC" }).format(date);
}

function formatDateLabel(expenseDate: string, language: AppLanguage) {
  const timestamp = Date.parse(`${expenseDate}T00:00:00Z`);

  if (!Number.isFinite(timestamp)) {
    return expenseDate;
  }

  return new Intl.DateTimeFormat(language === "ja" ? "ja-JP" : "en-US", { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" }).format(timestamp);
}

function formatAmount(amount: number, currency: string) {
  const normalizedCurrency = (currency || "USD").trim().toUpperCase();

  try {
    return new Intl.NumberFormat(undefined, { style: "currency", currency: normalizedCurrency, maximumFractionDigits: 2 }).format(amount);
  } catch {
    return `${normalizedCurrency} ${amount.toFixed(2)}`;
  }
}

function CountSpinner() {
  return (
    <span className="inline-flex h-9 w-9 items-center justify-center text-[var(--brand-primary)]" aria-label="Loading">
      <svg viewBox="0 0 20 20" fill="none" aria-hidden="true" className="h-7 w-7 animate-spin" xmlns="http://www.w3.org/2000/svg">
        <circle cx="10" cy="10" r="7" stroke="currentColor" strokeOpacity="0.2" strokeWidth="2" />
        <path d="M10 3a7 7 0 0 1 7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      </svg>
    </span>
  );
}

function StatCard({ label, value, detail, isLoading = false }: { label: string; value: string; detail: string; isLoading?: boolean }) {
  return (
    <div className="rounded-[24px] bg-white p-6 shadow-[0_20px_60px_rgba(15,23,42,0.06)]">
      <p className="text-[10px] font-semibold uppercase tracking-[0.28em] text-[#98a2b3]">{label}</p>
      <div className="mt-4 min-h-[2.25rem]">
        {isLoading ? <CountSpinner /> : <p className="text-3xl font-extrabold tracking-[-0.04em] text-[#101828]">{value}</p>}
      </div>
      <p className="mt-2 text-sm text-[#667085]">{detail}</p>
    </div>
  );
}

const modalInputClassName =
  "mt-2 w-full rounded-2xl border border-[#d0d5dd] bg-white px-4 py-3 text-sm text-[#101828] outline-none transition placeholder:text-[#98a2b3] placeholder:italic focus:border-[var(--brand-primary)] focus:ring-2 focus:ring-[var(--brand-primary-ring)]";
const modalLabelClassName = "text-[10px] font-semibold uppercase tracking-[0.22em] text-[#98a2b3]";

type ExpenseTrackerViewCopy = {
  financeEyebrow: string;
  pageTitle: string;
  pageDescription: string;
  newExpense: string;
  addVendorFirst: string;
  statSelectedMonth: string;
  statAllTimeSpend: string;
  statAllTimeSpendDetail: string;
  statExpenses: string;
  statExpensesDetail: (count: number) => string;
  statVendors: string;
  statVendorsDetail: string;
  monthlyExpenses: string;
  monthLabel: string;
  ownerLabel: string;
  allOwners: string;
  unknownOwner: string;
  colAddedBy: string;
  emptyMonthTitle: (month: string) => string;
  emptyMonthDetail: string;
  colDate: string;
  colVendor: string;
  colDescription: string;
  colAmount: string;
  unknownVendor: string;
  delete: string;
  edit: string;
  editTitle: string;
  editDescription: string;
  updateExpense: string;
  noCreatePermission: string;
  noEditPermission: string;
  noDeletePermission: string;
  total: string;
  monthlyBreakdown: string;
  emptyBreakdown: string;
  createEyebrow: string;
  createTitle: string;
  createDescription: string;
  vendorLabel: string;
  vendorPlaceholder: string;
  customerLabel: string;
  customerPlaceholder: string;
  noCustomer: string;
  colCustomer: string;
  newVendorOption: string;
  newVendorNameLabel: string;
  newVendorNamePlaceholder: string;
  newVendorCategoryLabel: string;
  newVendorCategoryPlaceholder: string;
  createVendorButton: string;
  amountLabel: string;
  dateLabel: string;
  descriptionLabel: string;
  descriptionPlaceholder: string;
  cancel: string;
  addExpense: string;
  savingExpense: string;
  invoicesLabel: string;
  invoicesHint: string;
  chooseInvoices: string;
  removeInvoice: string;
  invoiceTooLarge: (name: string) => string;
  invoicesTooLarge: string;
  colInvoices: string;
  noInvoices: string;
  invoiceCount: (count: number) => string;
};

const expenseTrackerViewCopy: Record<AppLanguage, ExpenseTrackerViewCopy> = {
  en: {
    financeEyebrow: "Finance",
    pageTitle: "Expenses",
    pageDescription: "Track vendor spend, log expenses against dates, and review monthly totals at a glance.",
    newExpense: "New expense",
    addVendorFirst: "Add a vendor first",
    statSelectedMonth: "Selected month",
    statAllTimeSpend: "All-time spend",
    statAllTimeSpendDetail: "Across every recorded expense",
    statExpenses: "Expenses",
    statExpensesDetail: count => `${count} in selected month`,
    statVendors: "Vendors",
    statVendorsDetail: "Active vendor records",
    monthlyExpenses: "Monthly expenses",
    monthLabel: "Month",
    ownerLabel: "Member",
    allOwners: "All members",
    unknownOwner: "Unknown",
    colAddedBy: "Added by",
    emptyMonthTitle: month => `No expenses for ${month}.`,
    emptyMonthDetail: "Use “New expense” to record vendor spend for this month.",
    colDate: "Date",
    colVendor: "Vendor",
    colDescription: "Description",
    colAmount: "Amount",
    unknownVendor: "Unknown vendor",
    delete: "Delete",
    edit: "Edit",
    editTitle: "Edit expense",
    editDescription: "Update the vendor, amount, date, or description.",
    updateExpense: "Save changes",
    noCreatePermission: "You do not have permission to create expenses.",
    noEditPermission: "You do not have permission to edit this expense.",
    noDeletePermission: "You do not have permission to delete expenses.",
    total: "Total",
    monthlyBreakdown: "Monthly breakdown",
    emptyBreakdown: "No expenses recorded yet.",
    createEyebrow: "New expense",
    createTitle: "Record an expense",
    createDescription: "Log spend against a vendor and date.",
    vendorLabel: "Vendor",
    vendorPlaceholder: "Select a vendor",
    customerLabel: "Customer",
    customerPlaceholder: "No customer",
    noCustomer: "—",
    colCustomer: "Customer",
    newVendorOption: "＋ New vendor",
    newVendorNameLabel: "New vendor name",
    newVendorNamePlaceholder: "Vendor name",
    newVendorCategoryLabel: "Category",
    newVendorCategoryPlaceholder: "e.g. Software, Travel",
    createVendorButton: "Create vendor",
    amountLabel: "Amount",
    dateLabel: "Date",
    descriptionLabel: "Description",
    descriptionPlaceholder: "What was this expense for?",
    cancel: "Cancel",
    addExpense: "Add expense",
    savingExpense: "Uploading…",
    invoicesLabel: "Invoices",
    invoicesHint: "Attach invoices — up to 10MB per file and 25MB in total.",
    chooseInvoices: "Add invoices",
    removeInvoice: "Remove",
    invoiceTooLarge: name => `${name} is larger than 10MB and was skipped.`,
    invoicesTooLarge: "Invoices cannot exceed 25MB in total.",
    colInvoices: "Invoices",
    noInvoices: "None",
    invoiceCount: count => (count === 1 ? "1 invoice" : `${count} invoices`),
  },
  ja: {
    financeEyebrow: "財務",
    pageTitle: "経費",
    pageDescription: "ベンダー支出を追跡し、日付ごとに経費を記録し、月次合計を一目で確認できます。",
    newExpense: "経費を追加",
    addVendorFirst: "先にベンダーを追加してください",
    statSelectedMonth: "選択月",
    statAllTimeSpend: "累計支出",
    statAllTimeSpendDetail: "記録された全経費の合計",
    statExpenses: "経費",
    statExpensesDetail: count => `選択月中 ${count} 件`,
    statVendors: "ベンダー",
    statVendorsDetail: "有効なベンダー件数",
    monthlyExpenses: "月別経費",
    monthLabel: "月",
    ownerLabel: "メンバー",
    allOwners: "全メンバー",
    unknownOwner: "不明",
    colAddedBy: "登録者",
    emptyMonthTitle: month => `${month} の経費はありません。`,
    emptyMonthDetail: "「経費を追加」からこの月のベンダー支出を記録してください。",
    colDate: "日付",
    colVendor: "ベンダー",
    colDescription: "説明",
    colAmount: "金額",
    unknownVendor: "不明なベンダー",
    delete: "削除",
    edit: "編集",
    editTitle: "経費を編集",
    editDescription: "ベンダー、金額、日付、説明を更新します。",
    updateExpense: "変更を保存",
    noCreatePermission: "経費を作成する権限がありません。",
    noEditPermission: "この経費を編集する権限がありません。",
    noDeletePermission: "経費を削除する権限がありません。",
    total: "合計",
    monthlyBreakdown: "月別内訳",
    emptyBreakdown: "経費がまだ記録されていません。",
    createEyebrow: "経費を追加",
    createTitle: "経費を記録",
    createDescription: "ベンダーと日付に対して支出を記録します。",
    vendorLabel: "ベンダー",
    vendorPlaceholder: "ベンダーを選択",
    customerLabel: "顧客",
    customerPlaceholder: "顧客なし",
    noCustomer: "—",
    colCustomer: "顧客",
    newVendorOption: "＋ 新しいベンダー",
    newVendorNameLabel: "新しいベンダー名",
    newVendorNamePlaceholder: "ベンダー名",
    newVendorCategoryLabel: "カテゴリ",
    newVendorCategoryPlaceholder: "例: ソフトウェア、出張",
    createVendorButton: "ベンダーを作成",
    amountLabel: "金額",
    dateLabel: "日付",
    descriptionLabel: "説明",
    descriptionPlaceholder: "この経費の内容は？",
    cancel: "キャンセル",
    addExpense: "経費を追加",
    savingExpense: "アップロード中…",
    invoicesLabel: "請求書",
    invoicesHint: "請求書を添付できます — 1 ファイル最大 10MB、合計最大 25MB。",
    chooseInvoices: "請求書を追加",
    removeInvoice: "削除",
    invoiceTooLarge: name => `${name} は 10MB を超えているためスキップされました。`,
    invoicesTooLarge: "請求書の合計は 25MB を超えられません。",
    colInvoices: "請求書",
    noInvoices: "なし",
    invoiceCount: count => `${count} 件の請求書`,
  },
};

export function ExpenseTrackerView({
  vendors,
  expenses,
  customers = [],
  currency,
  language,
  isLoading = false,
  canManageInvoices = false,
  canViewAllExpenses = false,
  canEditExpenses = false,
  canDeleteExpenses = false,
  canEditAllExpenses = false,
  canCreateVendors = false,
  currentUserEmail = null,
  members = [],
  onCreateExpense,
  onUpdateExpense,
  onDeleteExpense,
  onDeleteInvoice,
  onCreateVendor,
}: ExpenseTrackerViewProps) {
  const [selectedMonth, setSelectedMonth] = useState<string>(getCurrentMonthKey());
  const [ownerFilter, setOwnerFilter] = useState<string>("all");
  const [isCreateExpenseOpen, setIsCreateExpenseOpen] = useState(false);
  const [editingExpenseId, setEditingExpenseId] = useState<string | null>(null);
  const [expenseVendorId, setExpenseVendorId] = useState<string>("");
  const [expenseCustomerId, setExpenseCustomerId] = useState<string>("");
  const [expenseDescription, setExpenseDescription] = useState<string>("");
  const [expenseAmount, setExpenseAmount] = useState<string>("");
  const [expenseDate, setExpenseDate] = useState<string>(getTodayDate());
  const [expenseInvoices, setExpenseInvoices] = useState<File[]>([]);
  const [invoiceError, setInvoiceError] = useState<string | null>(null);
  const [isSubmittingExpense, setIsSubmittingExpense] = useState(false);
  const [isCreatingVendor, setIsCreatingVendor] = useState(false);
  const [newVendorName, setNewVendorName] = useState<string>("");
  const [newVendorCategory, setNewVendorCategory] = useState<string>("");
  const copy = expenseTrackerViewCopy[language];

  const normalizedCurrentUserEmail = (currentUserEmail ?? "").trim().toLowerCase();
  const canEditExpenseRow = (expense: Expense) => {
    if (canEditAllExpenses) {
      return true;
    }
    if (!canEditExpenses) {
      return false;
    }
    return normalizedCurrentUserEmail.length > 0 && (expense.createdBy ?? "").trim().toLowerCase() === normalizedCurrentUserEmail;
  };

  const vendorNameById = useMemo(() => new Map(vendors.map(vendor => [vendor.id, vendor.name])), [vendors]);

  const customerNameById = useMemo(() => new Map(customers.map(customer => [customer.id, customer.company])), [customers]);

  const sortedCustomers = useMemo(
    () => [...customers].sort((left, right) => left.company.localeCompare(right.company)),
    [customers],
  );

  const memberNameByEmail = useMemo(
    () => new Map(members.map(member => [member.email.trim().toLowerCase(), member.name])),
    [members],
  );

  const resolveOwnerName = useMemo(
    () => (email: string | null | undefined) => {
      const normalized = (email ?? "").trim();
      if (!normalized) {
        return copy.unknownOwner;
      }
      return memberNameByEmail.get(normalized.toLowerCase()) ?? normalized;
    },
    [copy.unknownOwner, memberNameByEmail],
  );

  const ownerOptions = useMemo(() => {
    const owners = new Map<string, string>();
    for (const expense of expenses) {
      const normalized = (expense.createdBy ?? "").trim();
      const value = normalized.toLowerCase();
      if (!value) {
        continue;
      }
      if (!owners.has(value)) {
        owners.set(value, resolveOwnerName(normalized));
      }
    }
    return [...owners.entries()]
      .map(([value, label]) => ({ value, label }))
      .sort((left, right) => left.label.localeCompare(right.label));
  }, [expenses, resolveOwnerName]);

  const filteredExpenses = useMemo(() => {
    if (!canViewAllExpenses || ownerFilter === "all") {
      return expenses;
    }
    return expenses.filter(expense => (expense.createdBy ?? "").trim().toLowerCase() === ownerFilter);
  }, [canViewAllExpenses, expenses, ownerFilter]);

  const sortedExpenses = useMemo(
    () => [...filteredExpenses].sort((left, right) => right.expenseDate.localeCompare(left.expenseDate) || right.createdAt.localeCompare(left.createdAt)),
    [filteredExpenses],
  );

  const monthlyTotals = useMemo(() => {
    const totals = new Map<string, number>();

    for (const expense of filteredExpenses) {
      const monthKey = getMonthKey(expense.expenseDate);
      totals.set(monthKey, (totals.get(monthKey) ?? 0) + expense.amount);
    }

    return [...totals.entries()].sort((left, right) => right[0].localeCompare(left[0]));
  }, [filteredExpenses]);

  const monthOptions = useMemo(() => {
    const months = new Set<string>([selectedMonth, getCurrentMonthKey(), ...expenses.map(expense => getMonthKey(expense.expenseDate))]);
    return [...months].filter(Boolean).sort((left, right) => right.localeCompare(left));
  }, [expenses, selectedMonth]);

  const monthExpenses = useMemo(
    () => sortedExpenses.filter(expense => getMonthKey(expense.expenseDate) === selectedMonth),
    [sortedExpenses, selectedMonth],
  );

  const monthTotal = useMemo(() => monthExpenses.reduce((sum, expense) => sum + expense.amount, 0), [monthExpenses]);
  const allTimeTotal = useMemo(() => filteredExpenses.reduce((sum, expense) => sum + expense.amount, 0), [filteredExpenses]);
  const maxMonthlyTotal = useMemo(() => monthlyTotals.reduce((max, [, total]) => Math.max(max, total), 0), [monthlyTotals]);

  const closeExpenseModal = () => {
    setIsCreateExpenseOpen(false);
    setEditingExpenseId(null);
    setExpenseVendorId("");
    setExpenseCustomerId("");
    setExpenseDescription("");
    setExpenseAmount("");
    setExpenseDate(getTodayDate());
    setExpenseInvoices([]);
    setInvoiceError(null);
    setIsCreatingVendor(false);
    setNewVendorName("");
    setNewVendorCategory("");
  };

  const handleOpenExpenseModal = () => {
    setEditingExpenseId(null);
    setExpenseVendorId(vendors[0]?.id ?? "");
    setExpenseCustomerId("");
    setExpenseDescription("");
    setExpenseAmount("");
    setExpenseDate(getTodayDate());
    setExpenseInvoices([]);
    setInvoiceError(null);
    setIsCreatingVendor(false);
    setNewVendorName("");
    setNewVendorCategory("");
    setIsCreateExpenseOpen(true);
  };

  const handleOpenEditModal = (expense: Expense) => {
    setEditingExpenseId(expense.id);
    setExpenseVendorId(expense.vendorId);
    setExpenseCustomerId(expense.customerId ?? "");
    setExpenseDescription(expense.description);
    setExpenseAmount(String(expense.amount));
    setExpenseDate(expense.expenseDate || getTodayDate());
    setExpenseInvoices([]);
    setInvoiceError(null);
    setIsCreatingVendor(false);
    setNewVendorName("");
    setNewVendorCategory("");
    setIsCreateExpenseOpen(true);
  };

  const handleVendorSelectChange = (value: string) => {
    if (value === NEW_VENDOR_VALUE) {
      setExpenseVendorId(NEW_VENDOR_VALUE);
      setIsCreatingVendor(true);
      return;
    }

    setIsCreatingVendor(false);
    setNewVendorName("");
    setNewVendorCategory("");
    setExpenseVendorId(value);
  };

  const handleCreateNewVendor = () => {
    const name = newVendorName.trim();

    if (!name || !onCreateVendor) {
      return;
    }

    const createdVendor = onCreateVendor({ name, category: newVendorCategory.trim() });

    if (createdVendor) {
      setExpenseVendorId(createdVendor.id);
    }

    setIsCreatingVendor(false);
    setNewVendorName("");
    setNewVendorCategory("");
  };

  const handleInvoiceSelection = (event: ChangeEvent<HTMLInputElement>) => {
    const incoming = Array.from(event.target.files ?? []);
    event.target.value = "";

    if (incoming.length === 0) {
      return;
    }

    let error: string | null = null;
    const accepted: File[] = [];

    for (const file of incoming) {
      if (file.size > MAX_INVOICE_FILE_BYTES) {
        error = copy.invoiceTooLarge(file.name);
        continue;
      }

      accepted.push(file);
    }

    setExpenseInvoices(current => {
      const combined = [...current];

      for (const file of accepted) {
        const isDuplicate = combined.some(existing => existing.name === file.name && existing.size === file.size);

        if (!isDuplicate) {
          combined.push(file);
        }
      }

      const totalBytes = combined.reduce((sum, file) => sum + file.size, 0);

      if (totalBytes > MAX_INVOICE_TOTAL_BYTES) {
        setInvoiceError(copy.invoicesTooLarge);
        return current;
      }

      setInvoiceError(error);
      return combined;
    });
  };

  const handleRemoveInvoice = (index: number) => {
    setInvoiceError(null);
    setExpenseInvoices(current => current.filter((_, position) => position !== index));
  };

  const handleAddExpense = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const amountValue = Number.parseFloat(expenseAmount);

    if (!expenseVendorId || expenseVendorId === NEW_VENDOR_VALUE || !Number.isFinite(amountValue) || isSubmittingExpense) {
      return;
    }

    setIsSubmittingExpense(true);

    try {
      const input: ExpenseCreateInput = {
        vendorId: expenseVendorId,
        customerId: expenseCustomerId || null,
        description: expenseDescription.trim(),
        amount: amountValue,
        expenseDate: expenseDate || getTodayDate(),
      };

      if (editingExpenseId) {
        await onUpdateExpense?.(editingExpenseId, input, expenseInvoices);
      } else {
        await onCreateExpense(input, expenseInvoices);
      }
      closeExpenseModal();
    } finally {
      setIsSubmittingExpense(false);
    }
  };

  return (
    <>
      <section className="space-y-8 p-8">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.32em] text-[#98a2b3]">{copy.financeEyebrow}</p>
            <h1 className="mt-3 font-headline text-5xl font-extrabold tracking-[-0.06em] text-[#101828]">{copy.pageTitle}</h1>
            <p className="mt-3 max-w-3xl text-sm text-[#667085]">{copy.pageDescription}</p>
          </div>

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={handleOpenExpenseModal}
              disabled={(vendors.length === 0 && !canCreateVendors) || !canEditExpenses}
              title={!canEditExpenses ? copy.noCreatePermission : vendors.length === 0 && !canCreateVendors ? copy.addVendorFirst : undefined}
              className="brand-primary-button flex items-center gap-2 rounded-xl px-6 py-2.5 text-sm font-bold transition-all disabled:cursor-not-allowed disabled:opacity-50"
            >
              <span>＋</span>
              <span>{copy.newExpense}</span>
            </button>
          </div>
        </div>

        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          <StatCard label={copy.statSelectedMonth} value={formatAmount(monthTotal, currency)} detail={formatMonthLabel(selectedMonth, language)} isLoading={isLoading} />
          <StatCard label={copy.statAllTimeSpend} value={formatAmount(allTimeTotal, currency)} detail={copy.statAllTimeSpendDetail} isLoading={isLoading} />
          <StatCard label={copy.statExpenses} value={String(filteredExpenses.length)} detail={copy.statExpensesDetail(monthExpenses.length)} isLoading={isLoading} />
          <StatCard label={copy.statVendors} value={String(vendors.length)} detail={copy.statVendorsDetail} isLoading={isLoading} />
        </div>

        <div className="grid grid-cols-1 gap-8 xl:grid-cols-[minmax(0,1fr)_minmax(0,420px)]">
          <div className="rounded-[28px] bg-white p-7 shadow-[0_18px_50px_rgba(15,23,42,0.06)]">
            <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
              <div>
                <h2 className="font-headline text-2xl font-extrabold tracking-[-0.04em] text-[#101828]">{copy.monthlyExpenses}</h2>
                <p className="mt-1 text-sm text-[#667085]">{formatMonthLabel(selectedMonth, language)} · {formatAmount(monthTotal, currency)}</p>
              </div>

              <label className="flex items-center gap-2 text-sm text-[#475467]">
                <span className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#98a2b3]">{copy.monthLabel}</span>
                <select
                  value={selectedMonth}
                  onChange={event => setSelectedMonth(event.target.value)}
                  className="rounded-full border border-[#d0d5dd] bg-white px-4 py-2 text-sm font-semibold text-[#101828] outline-none transition focus:border-[var(--brand-primary)] focus:ring-2 focus:ring-[var(--brand-primary-ring)]"
                >
                  {monthOptions.map(monthKey => (
                    <option key={monthKey} value={monthKey}>
                      {formatMonthLabel(monthKey, language)}
                    </option>
                  ))}
                </select>
              </label>

              {canViewAllExpenses ? (
                <label className="flex items-center gap-2 text-sm text-[#475467]">
                  <span className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#98a2b3]">{copy.ownerLabel}</span>
                  <select
                    value={ownerFilter}
                    onChange={event => setOwnerFilter(event.target.value)}
                    className="rounded-full border border-[#d0d5dd] bg-white px-4 py-2 text-sm font-semibold text-[#101828] outline-none transition focus:border-[var(--brand-primary)] focus:ring-2 focus:ring-[var(--brand-primary-ring)]"
                  >
                    <option value="all">{copy.allOwners}</option>
                    {ownerOptions.map(owner => (
                      <option key={owner.value} value={owner.value}>
                        {owner.label}
                      </option>
                    ))}
                  </select>
                </label>
              ) : null}
            </div>

            {monthExpenses.length === 0 ? (
              <div className="rounded-[24px] border border-dashed border-[#d0d5dd] bg-[#fcfcfd] px-6 py-12 text-center">
                <p className="text-sm font-semibold text-[#101828]">{copy.emptyMonthTitle(formatMonthLabel(selectedMonth, language))}</p>
                <p className="mt-2 text-sm text-[#667085]">{copy.emptyMonthDetail}</p>
              </div>
            ) : (
              <div className="overflow-hidden rounded-[24px] border border-[rgba(0,0,0,0.04)]">
                <table className="w-full border-collapse text-left text-sm">
                  <thead>
                    <tr className="bg-[#fcfcfd] text-[10px] uppercase tracking-[0.22em] text-[#98a2b3]">
                      <th className="px-5 py-3 font-semibold">{copy.colDate}</th>
                      <th className="px-5 py-3 font-semibold">{copy.colVendor}</th>
                      <th className="px-5 py-3 font-semibold">{copy.colCustomer}</th>
                      {canViewAllExpenses ? <th className="px-5 py-3 font-semibold">{copy.colAddedBy}</th> : null}
                      <th className="px-5 py-3 font-semibold">{copy.colDescription}</th>
                      <th className="px-5 py-3 font-semibold">{copy.colInvoices}</th>
                      <th className="px-5 py-3 text-right font-semibold">{copy.colAmount}</th>
                      <th className="px-5 py-3" aria-label="Actions" />
                    </tr>
                  </thead>
                  <tbody>
                    {monthExpenses.map(expense => (
                      <tr key={expense.id} className="border-t border-[#f2f4f7]">
                        <td className="px-5 py-4 text-[#475467]">{formatDateLabel(expense.expenseDate, language)}</td>
                        <td className="px-5 py-4 font-semibold text-[#101828]">{vendorNameById.get(expense.vendorId) ?? copy.unknownVendor}</td>
                        <td className="px-5 py-4 text-[#667085]">{expense.customerId ? (customerNameById.get(expense.customerId) ?? copy.noCustomer) : copy.noCustomer}</td>
                        {canViewAllExpenses ? <td className="px-5 py-4 text-[#667085]">{resolveOwnerName(expense.createdBy)}</td> : null}
                        <td className="px-5 py-4 text-[#667085]">{expense.description || "—"}</td>
                        <td className="px-5 py-4 text-[#667085]">
                          {(expense.invoices?.length ?? 0) === 0 ? (
                            <span className="text-[#98a2b3]">{copy.noInvoices}</span>
                          ) : (
                            <ul className="flex flex-col gap-1.5">
                              {expense.invoices!.map(invoice => (
                                <li key={invoice.key} className="flex items-center gap-2">
                                  <a
                                    href={invoice.downloadUrl}
                                    target="_blank"
                                    rel="noreferrer"
                                    title={`${invoice.fileName} · ${formatFileSize(invoice.size)}`}
                                    className="max-w-[180px] truncate text-xs font-semibold text-[var(--brand-primary)] transition-colors hover:underline"
                                  >
                                    {invoice.fileName}
                                  </a>
                                  {canManageInvoices && onDeleteInvoice ? (
                                    <button
                                      type="button"
                                      onClick={() => onDeleteInvoice(expense.id, invoice.key)}
                                      aria-label={copy.removeInvoice}
                                      className="text-xs font-semibold text-[#b42318] transition-colors hover:underline"
                                    >
                                      ×
                                    </button>
                                  ) : null}
                                </li>
                              ))}
                            </ul>
                          )}
                        </td>
                        <td className="px-5 py-4 text-right font-bold text-[#101828]">{formatAmount(expense.amount, currency)}</td>
                        <td className="px-5 py-4 text-right">
                          <div className="flex items-center justify-end gap-3">
                            <button
                              type="button"
                              onClick={() => handleOpenEditModal(expense)}
                              disabled={!canEditExpenseRow(expense)}
                              title={!canEditExpenseRow(expense) ? copy.noEditPermission : undefined}
                              className="text-xs font-semibold text-[var(--brand-primary)] transition-colors hover:underline disabled:cursor-not-allowed disabled:text-[#98a2b3] disabled:no-underline"
                            >
                              {copy.edit}
                            </button>
                            <button
                              type="button"
                              onClick={() => onDeleteExpense(expense.id)}
                              disabled={!canDeleteExpenses}
                              title={!canDeleteExpenses ? copy.noDeletePermission : undefined}
                              className="text-xs font-semibold text-[#b42318] transition-colors hover:underline disabled:cursor-not-allowed disabled:text-[#98a2b3] disabled:no-underline"
                            >
                              {copy.delete}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t border-[rgba(0,0,0,0.06)] bg-[#fcfcfd]">
                      <td className="px-5 py-4 text-[10px] font-semibold uppercase tracking-[0.22em] text-[#98a2b3]" colSpan={canViewAllExpenses ? 6 : 5}>
                        {copy.total}
                      </td>
                      <td className="px-5 py-4 text-right text-lg font-extrabold text-[#101828]">{formatAmount(monthTotal, currency)}</td>
                      <td className="px-5 py-4" />
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>

          <div className="flex flex-col gap-8">
            <div className="rounded-[28px] bg-white p-7 shadow-[0_18px_50px_rgba(15,23,42,0.06)]">
              <h2 className="mb-5 font-headline text-2xl font-extrabold tracking-[-0.04em] text-[#101828]">{copy.monthlyBreakdown}</h2>
              <ul className="flex flex-col gap-3">
                {monthlyTotals.length === 0 ? (
                  <li className="rounded-[20px] border border-dashed border-[#d0d5dd] bg-[#fcfcfd] px-4 py-8 text-center text-sm text-[#667085]">{copy.emptyBreakdown}</li>
                ) : (
                  monthlyTotals.map(([monthKey, total]) => (
                    <li key={monthKey}>
                      <button
                        type="button"
                        onClick={() => setSelectedMonth(monthKey)}
                        className={`w-full rounded-[20px] border px-4 py-3 text-left transition-colors ${monthKey === selectedMonth ? "border-[var(--brand-primary)] bg-[var(--brand-primary-ring)]" : "border-[rgba(0,0,0,0.04)] bg-[#fcfcfd] hover:border-[#cfd4dc]"}`}
                      >
                        <div className="flex items-center justify-between gap-3">
                          <span className="text-sm font-semibold text-[#101828]">{formatMonthLabel(monthKey, language)}</span>
                          <span className="text-sm font-bold text-[#101828]">{formatAmount(total, currency)}</span>
                        </div>
                        <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-[#eef0f3]">
                          <div
                            className="h-full rounded-full bg-[var(--brand-primary)]"
                            style={{ width: `${maxMonthlyTotal > 0 ? Math.max(6, Math.round((total / maxMonthlyTotal) * 100)) : 0}%` }}
                          />
                        </div>
                      </button>
                    </li>
                  ))
                )}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <AnimatedModal
        open={isCreateExpenseOpen}
        onClose={closeExpenseModal}
        backdropAriaLabel="Close new expense dialog"
        overlayClassName="fixed inset-0 z-[80] flex items-center justify-center bg-[#101828]/36 p-6 backdrop-blur-[4px]"
        dialogClassName="app-modal-dialog relative z-10 w-full max-w-2xl rounded-[32px] border border-white/70 bg-[#fcfcfd] p-7 shadow-[0_32px_100px_rgba(15,23,42,0.24)]"
      >
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.28em] text-slate-400">{editingExpenseId ? copy.edit : copy.createEyebrow}</p>
            <h2 className="mt-2 font-headline text-3xl font-extrabold tracking-[-0.05em] text-[#101828]">{editingExpenseId ? copy.editTitle : copy.createTitle}</h2>
            <p className="mt-2 text-sm text-[#667085]">{editingExpenseId ? copy.editDescription : copy.createDescription}</p>
          </div>
          <button
            type="button"
            onClick={closeExpenseModal}
            className="flex h-11 w-11 items-center justify-center rounded-2xl bg-white text-lg font-semibold text-slate-500 shadow-[0_12px_24px_rgba(15,23,42,0.08)] transition-colors hover:bg-[#f3f4f6] hover:text-[#1a1c1d]"
          >
            ×
          </button>
        </div>

        <form onSubmit={handleAddExpense} className="mt-6 space-y-6">
          <div className="grid gap-4 md:grid-cols-2">
            <label className="block md:col-span-2">
              <span className={modalLabelClassName}>{copy.vendorLabel}</span>
              <select
                value={expenseVendorId}
                onChange={event => handleVendorSelectChange(event.target.value)}
                required
                className={modalInputClassName}
              >
                <option value="" disabled>
                  {copy.vendorPlaceholder}
                </option>
                {vendors.map(vendor => (
                  <option key={vendor.id} value={vendor.id}>
                    {vendor.name}
                  </option>
                ))}
                {canCreateVendors && onCreateVendor ? (
                  <option value={NEW_VENDOR_VALUE}>{copy.newVendorOption}</option>
                ) : null}
              </select>

              {isCreatingVendor ? (
                <div className="mt-3 space-y-3 rounded-2xl border border-[#eef0f3] bg-white p-4">
                  <label className="block">
                    <span className={modalLabelClassName}>{copy.newVendorNameLabel}</span>
                    <input
                      type="text"
                      value={newVendorName}
                      onChange={event => setNewVendorName(event.target.value)}
                      placeholder={copy.newVendorNamePlaceholder}
                      className={modalInputClassName}
                    />
                  </label>
                  <label className="block">
                    <span className={modalLabelClassName}>{copy.newVendorCategoryLabel}</span>
                    <input
                      type="text"
                      value={newVendorCategory}
                      onChange={event => setNewVendorCategory(event.target.value)}
                      placeholder={copy.newVendorCategoryPlaceholder}
                      className={modalInputClassName}
                    />
                  </label>
                  <button
                    type="button"
                    onClick={handleCreateNewVendor}
                    disabled={newVendorName.trim() === ""}
                    className="brand-primary-button inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-bold transition-all disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {copy.createVendorButton}
                  </button>
                </div>
              ) : null}
            </label>

            <label className="block">
              <span className={modalLabelClassName}>{copy.amountLabel}</span>
              <input
                type="number"
                inputMode="decimal"
                min="0"
                step="0.01"
                value={expenseAmount}
                onChange={event => setExpenseAmount(event.target.value)}
                placeholder="0.00"
                required
                className={modalInputClassName}
              />
            </label>

            <label className="block">
              <span className={modalLabelClassName}>{copy.dateLabel}</span>
              <input
                type="date"
                value={expenseDate}
                onChange={event => setExpenseDate(event.target.value)}
                required
                className={modalInputClassName}
              />
            </label>

            <label className="block md:col-span-2">
              <span className={modalLabelClassName}>{copy.descriptionLabel}</span>
              <input
                type="text"
                value={expenseDescription}
                onChange={event => setExpenseDescription(event.target.value)}
                placeholder={copy.descriptionPlaceholder}
                className={modalInputClassName}
              />
            </label>

            <label className="block md:col-span-2">
              <span className={modalLabelClassName}>{copy.customerLabel}</span>
              <select
                value={expenseCustomerId}
                onChange={event => setExpenseCustomerId(event.target.value)}
                className={modalInputClassName}
              >
                <option value="">{copy.customerPlaceholder}</option>
                {sortedCustomers.map(customer => (
                  <option key={customer.id} value={customer.id}>
                    {customer.company}
                  </option>
                ))}
              </select>
            </label>

            <div className="block md:col-span-2">
              <span className={modalLabelClassName}>{copy.invoicesLabel}</span>
              <p className="mt-1 text-xs text-[#667085]">{copy.invoicesHint}</p>
              <label className="mt-2 inline-flex cursor-pointer items-center gap-2 rounded-2xl border border-dashed border-[#d0d5dd] bg-white px-4 py-3 text-sm font-semibold text-[#475467] transition-colors hover:border-[var(--brand-primary)]">
                <span>＋ {copy.chooseInvoices}</span>
                <input type="file" multiple onChange={handleInvoiceSelection} className="sr-only" />
              </label>

              {invoiceError ? <p className="mt-2 text-xs font-semibold text-[#b42318]">{invoiceError}</p> : null}

              {expenseInvoices.length > 0 ? (
                <ul className="mt-3 flex flex-col gap-2">
                  {expenseInvoices.map((file, index) => (
                    <li
                      key={`${file.name}-${file.size}-${index}`}
                      className="flex items-center justify-between gap-3 rounded-2xl border border-[#eef0f3] bg-white px-4 py-2.5"
                    >
                      <span className="min-w-0 flex-1 truncate text-sm text-[#101828]" title={file.name}>{file.name}</span>
                      <span className="text-xs text-[#98a2b3]">{formatFileSize(file.size)}</span>
                      <button
                        type="button"
                        onClick={() => handleRemoveInvoice(index)}
                        className="text-xs font-semibold text-[#b42318] transition-colors hover:underline"
                      >
                        {copy.removeInvoice}
                      </button>
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          </div>

          <div className="flex flex-wrap justify-end gap-2">
            <button
              type="button"
              onClick={closeExpenseModal}
              disabled={isSubmittingExpense}
              className="rounded-xl px-4 py-2.5 text-sm font-semibold text-[#475467] transition-colors hover:bg-[#f3f4f6] disabled:cursor-not-allowed disabled:opacity-50"
            >
              {copy.cancel}
            </button>
            <button
              type="submit"
              disabled={!expenseVendorId || expenseVendorId === NEW_VENDOR_VALUE || expenseAmount.trim() === "" || isSubmittingExpense}
              className="brand-primary-button inline-flex items-center gap-2 rounded-xl px-5 py-2.5 text-sm font-bold transition-all disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isSubmittingExpense ? (
                <>
                  <svg viewBox="0 0 20 20" fill="none" aria-hidden="true" className="h-4 w-4 animate-spin" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="10" cy="10" r="7" stroke="currentColor" strokeOpacity="0.2" strokeWidth="2" />
                    <path d="M10 3a7 7 0 0 1 7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  </svg>
                  <span>{copy.savingExpense}</span>
                </>
              ) : (
                editingExpenseId ? copy.updateExpense : copy.addExpense
              )}
            </button>
          </div>
        </form>
      </AnimatedModal>
    </>
  );
}
